/**
 * Publish trigger — POST /api/admin/publish fires the CF Builds deploy
 * hook so the static site rebuilds (~30-60s to live).
 *
 * CF Builds dedupes incoming triggers while a build is starting, and
 * queues triggers that arrive while one is running — so rapid sequential
 * publishes coalesce into at most two builds.
 */

import { API_BASE } from './config';
import { getSession } from './auth';

let lastPublishAt = 0;
const MIN_INTERVAL_MS = 4_000; // light client-side throttle

export async function triggerPublish(): Promise<{ ok: boolean; reason?: string }> {
  const now = Date.now();
  if (now - lastPublishAt < MIN_INTERVAL_MS) {
    return { ok: true, reason: 'throttled (CF Builds will dedupe anyway)' };
  }
  lastPublishAt = now;

  try {
    const session = getSession();
    if (!session) return { ok: false, reason: 'not signed in' };
    const res = await fetch(`${API_BASE}/api/admin/publish`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      return { ok: false, reason: `${res.status} ${detail}` };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: (e as Error).message };
  }
}

/**
 * Format a relative-time hint for the publish status pill.
 * Example: "publishing… (live in ~60s)" or "live · 2m ago".
 */
export function formatPublishStatus(state: 'idle' | 'pending' | 'live', sinceMs: number | null): string {
  if (state === 'pending') return 'Publishing — live in ~60s';
  if (state === 'live' && sinceMs !== null) {
    const elapsed = Math.max(0, Date.now() - sinceMs);
    const seconds = Math.floor(elapsed / 1000);
    if (seconds < 60) return `Live · just now`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Live · ${minutes}m ago`;
    return `Live · ${Math.floor(minutes / 60)}h ago`;
  }
  return '';
}
